import { useEffect, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { getAssignmentsManagementInitData } from "@containers/upf-assign/upf-assign-actions";
import { getAssignments } from '@containers/gateways/gateways-actions';
import { resetEntity } from '@containers/entities/entities-reducer';
import { ENTITY_NAMES } from '@containers/entities/constants'

import { getConfiguration } from '@helpers/utils';
import useCourseVersion from '@helpers/hooks/use-course-version';
import useAssignmentResultType from '@helpers/hooks/use-assignment-result-type';

import useInterval from './use-interval';

const usePollingV2 = () => {
  const DEFAULT_POLL_DELAY = 10000;
  const POLL_DELAY = getConfiguration('pollDelay') || DEFAULT_POLL_DELAY;

  const [pollingTime, setPollingTime] = useState(POLL_DELAY);

  /**
   * Store
   */
  const isLoadingAssignments = useSelector(state => state.gatewaysReducer.isLoadingAssignments);
  const isLoadingGenericCourses = useSelector(state => state.UPFAssignReducer.isLoadingGenericCourses);
  const sortedStudentIds = useSelector(state => state.UPFAssignReducer.sortedStudentIds);
  const selectedProduct = useSelector(state => state.product, shallowEqual);
  const selectedLearningJourneyIds = useSelector(state => state.UPFAssignReducer.selectedLearningJourneyIds);
  const selectedAssignmentHashKeys = useSelector(
    state => state.UPFAssignReducer.selectedAssignmentHashKeys,
    shallowEqual
  );

  /**
   * Local variables
   */
  const pausePolling = Object.values(selectedAssignmentHashKeys).length > 0;
  const studentUserProfileIds = [...sortedStudentIds].sort();

  /**
   * Hooks
   */
  const dispatch = useDispatch();
  const { selectedClassroomId, selectedCourseId, selectedCourseVersion } = useCourseVersion();
  const { assignmentResultType } = useAssignmentResultType();

  /**
   * Functions
   */
  const getAssignmentsHandler = () => {
    const shouldFetching =
      !pausePolling &&
      !isLoadingAssignments &&
      !isLoadingGenericCourses &&
      selectedClassroomId &&
      selectedCourseId &&
      selectedCourseVersion &&
      selectedProduct.id;

    if (shouldFetching) {
      dispatch(
        getAssignments({
          assignmentResultType,
          classId: selectedClassroomId,
          courseId: selectedCourseId,
          courseVersion: selectedCourseVersion,
          productId: selectedProduct.id
        })
      );
    }
  };

  /**
   * Effects
   */

  // get the initial data
  useEffect(() => {
    dispatch(getAssignmentsManagementInitData(selectedProduct.id));
  }, []);

  useEffect(() => {
    const stopPolling = pausePolling || isLoadingAssignments || isLoadingGenericCourses;
    setPollingTime(stopPolling ? null : POLL_DELAY);
  }, [pausePolling, isLoadingAssignments, isLoadingGenericCourses]);

  // dispatch action to poll result data in very POLL_DELAY secs
  useInterval(() => {
    getAssignmentsHandler();
  }, pollingTime);

  // once we have the initial data then go and fetch student assignments
  useEffect(() => {
    getAssignmentsHandler();
  }, [
    selectedLearningJourneyIds.toString(),
    studentUserProfileIds.toString(),
    isLoadingGenericCourses,
    selectedClassroomId,
    selectedCourseId,
    selectedCourseVersion
  ]);

  useEffect(() => {
    dispatch(resetEntity({ entityName: ENTITY_NAMES.ASSIGNMENTS }));
    getAssignmentsHandler();
  }, [assignmentResultType]);
};

export default usePollingV2;



// WEBPACK FOOTER //
// ./helpers/hooks/use-polling-v2.js